import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getDepartmentSubmitStatus,
  type DepartmentSubmitStatusRow,
  type DepartmentSubmitStatusSummary,
} from '@/api/purchase';
import MonthPicker, { currentMonthValue } from '@/components/base/MonthPicker';
import { isPurchasePlanManager } from '@/utils/auth';

type SubmitFilter = 'all' | 'submitted' | 'unsubmitted';

const SUBMIT_FILTERS: Array<{ id: SubmitFilter; label: string }> = [
  { id: 'all', label: '全部' },
  { id: 'submitted', label: '已提交' },
  { id: 'unsubmitted', label: '未提交' },
];

function parseMonthParam(value: string | null): string {
  const raw = String(value || '').trim();
  if (/^\d{4}-\d{2}$/.test(raw)) return raw;
  if (/^\d{6}$/.test(raw)) return `${raw.slice(0, 4)}-${raw.slice(4, 6)}`;
  return currentMonthValue();
}

function formatAmount(v: unknown): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getStatusTag(
  row: DepartmentSubmitStatusRow,
): { color: string; label: string; icon: string } {
  if (!row.is_submitted) {
    return {
      color: '#ed4014',
      label: row.status_text || '未提交',
      icon: 'ri-error-warning-line',
    };
  }
  const n = Number(row.status);
  const label = row.status_text || '-';
  if (n === 10) return { color: '#19be6b', label, icon: 'ri-checkbox-circle-line' };
  if (n === 1) return { color: '#2d8cf0', label, icon: 'ri-loader-4-line' };
  if (n === 0) return { color: '#33bcb7', label, icon: 'ri-time-line' };
  if (n === 2) return { color: '#ed4014', label, icon: 'ri-close-circle-line' };
  return { color: '#33bcb7', label, icon: 'ri-time-line' };
}

export default function PurchasePlanDepartmentSubmitStatus() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isManager = isPurchasePlanManager();

  const [month, setMonth] = useState(() => parseMonthParam(searchParams.get('month')));
  const [submitFilter, setSubmitFilter] = useState<SubmitFilter>('all');
  const [keyword, setKeyword] = useState('');

  const [rows, setRows] = useState<DepartmentSubmitStatusRow[]>([]);
  const [summary, setSummary] = useState<DepartmentSubmitStatusSummary | null>(null);
  const [monthLabel, setMonthLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const syncMonthToUrl = useCallback(
    (nextMonth: string) => {
      const current = parseMonthParam(searchParams.get('month'));
      if (current === nextMonth) return;
      const next = new URLSearchParams(searchParams);
      next.set('month', nextMonth);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleMonthChange = (value: string) => {
    const next = parseMonthParam(value);
    setMonth(next);
    syncMonthToUrl(next);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await getDepartmentSubmitStatus({ month });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setRows([]);
        setSummary(null);
        setLoadError(res.message || res.msg || '全院提交情况加载失败');
        return;
      }
      setRows(Array.isArray(res.data) ? res.data : []);
      setSummary(res.summary || null);
      setMonthLabel(String(res.apply_month_text || '').trim());
    } catch {
      setRows([]);
      setSummary(null);
      setLoadError('全院提交情况加载失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const filteredRows = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return rows.filter((row) => {
      if (submitFilter === 'submitted' && !row.is_submitted) return false;
      if (submitFilter === 'unsubmitted' && row.is_submitted) return false;
      if (!kw) return true;
      const name = String(row.department_name || '').toLowerCase();
      const account = String(row.account_name || '').toLowerCase();
      return name.includes(kw) || account.includes(kw);
    });
  }, [keyword, rows, submitFilter]);

  const title = useMemo(() => {
    const label = monthLabel || `${month.slice(0, 4)}年${month.slice(5, 7)}月`;
    return `${label}全院提交情况`;
  }, [month, monthLabel]);

  if (!isManager) {
    return <Navigate to="/403" replace />;
  }

  return (
    <div className="flex-1 bg-background-100">
      <div className="px-4 md:px-6 pb-4 md:pb-6">
        <div className="bg-white rounded-lg border border-background-200 p-4 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-44 shrink-0">
              <MonthPicker
                value={month}
                onChange={handleMonthChange}
                placeholder="选择计划月份"
              />
            </div>
            <div className="relative min-w-[12rem] max-w-xs flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索科室 / 提交人"
                className="w-full h-[38px] pl-9 pr-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/purchase-plan/month-details?month=${encodeURIComponent(month)}`,
                )
              }
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-300 text-xs text-foreground-600 hover:bg-background-50 shrink-0 ml-auto"
            >
              <i className="ri-arrow-left-line" />
              返回月汇总
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-foreground-900 truncate">{title}</h1>
              {summary ? (
                <p className="text-xs text-foreground-500 mt-0.5">
                  启用科室 {Number(summary.department_count) || 0} 个
                  <span className="mx-1.5 text-foreground-300">|</span>
                  已提交{' '}
                  <span className="text-primary-600 font-medium">
                    {Number(summary.submitted_count) || 0}
                  </span>
                  <span className="mx-1.5 text-foreground-300">|</span>
                  未提交{' '}
                  <span className="text-orange-600 font-medium">
                    {Number(summary.unsubmitted_count) || 0}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              {SUBMIT_FILTERS.map((f) => {
                const active = submitFilter === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setSubmitFilter(f.id)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                      active
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
              <thead>
                <tr className="bg-[#f8f8f9]">
                  <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap w-14">
                    序号
                  </th>
                  <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    计划月份
                  </th>
                  <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    科室
                  </th>
                  <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    提交人
                  </th>
                  <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    计划数量
                  </th>
                  <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    计划金额
                  </th>
                  <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    提交状态
                  </th>
                  <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap w-24">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="py-16 text-center text-foreground-400 text-sm">
                      <i className="ri-loader-4-line animate-spin mr-1.5" />
                      正在加载全院提交情况...
                    </td>
                  </tr>
                ) : loadError ? (
                  <tr>
                    <td colSpan={8} className="py-16 text-center text-sm">
                      <p className="text-foreground-500 mb-2">{loadError}</p>
                      <button
                        type="button"
                        onClick={() => void fetchList()}
                        className="text-primary-600 hover:text-primary-700 text-xs"
                      >
                        重新加载
                      </button>
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-16 text-center text-foreground-400 text-sm">
                      {rows.length === 0 ? '该月暂无科室提交情况' : '没有符合筛选条件的科室'}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row, idx) => {
                    const tag = getStatusTag(row);
                    const flowId = Number(row.flow_id);
                    const canOpen = Number.isFinite(flowId) && flowId > 0;
                    return (
                      <tr
                        key={`${row.department_id}-${idx}`}
                        className="hover:bg-background-50 transition-colors"
                      >
                        <td className="py-2.5 px-3 text-center text-foreground-500 text-xs">
                          {idx + 1}
                        </td>
                        <td className="py-2.5 px-3 text-foreground-600 text-xs whitespace-nowrap">
                          {row.apply_month_text || '-'}
                        </td>
                        <td className="py-2.5 px-3 text-foreground-800 text-xs max-w-[180px] truncate">
                          {row.department_name || '-'}
                        </td>
                        <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[120px] truncate">
                          {row.account_name || (row.is_submitted ? '-' : '—')}
                        </td>
                        <td className="py-2.5 px-3 text-center text-foreground-700 text-xs whitespace-nowrap">
                          {Number(row.plan_quantity) || 0}
                        </td>
                        <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap tabular-nums">
                          ¥{formatAmount(row.plan_amount)}
                        </td>
                        <td className="py-2.5 px-3 text-center whitespace-nowrap">
                          <span
                            className="inline-flex items-center gap-1 text-xs font-medium"
                            style={{ color: tag.color }}
                          >
                            <i className={tag.icon} />
                            {tag.label}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center whitespace-nowrap">
                          {canOpen ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/purchase-plan/${flowId}`)}
                              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                            >
                              查看计划
                            </button>
                          ) : (
                            <span className="text-xs text-foreground-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!loading && !loadError && filteredRows.length > 0 ? (
            <div className="px-5 py-3 border-t border-background-100 text-xs text-foreground-500">
              当前显示 {filteredRows.length} 个科室
              {filteredRows.length !== rows.length ? `（共 ${rows.length} 个）` : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
